import { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';
import {
  COMMERCE_FAMILIES,
  COMMERCE_METIERS,
  normalizeCategorieId,
} from '../config/commerce-categories';

function adaptFallbackMetiers() {
  return COMMERCE_METIERS.map((m) => ({
    ...m,
    familyId: m.family,
  }));
}

function adaptFallbackFamilies() {
  return COMMERCE_FAMILIES.map((f) => ({
    ...f,
    actif: true,
    deprecated: false,
    archivedAt: null,
    system: false,
    usageCount: 0,
  }));
}

/**
 * Cache taxonomie commerce — GET API avec fallback statique.
 */
export function useCommerceTaxonomy({ autoFetch = true } = {}) {
  const [families, setFamilies] = useState([]);
  const [metiers, setMetiers] = useState([]);
  const [loading, setLoading] = useState(Boolean(autoFetch));
  const [error, setError] = useState(null);
  const [source, setSource] = useState('api');

  const applyFallback = useCallback(() => {
    setFamilies(adaptFallbackFamilies());
    setMetiers(adaptFallbackMetiers());
    setSource('fallback');
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getCommerceTaxonomy({ includeInactive: '1' });
      const fams = Array.isArray(data?.families) ? data.families : [];
      const mets = Array.isArray(data?.metiers) ? data.metiers : [];
      if (!fams.length && !mets.length) {
        applyFallback();
        setError(new Error('Taxonomie vide'));
      } else {
        setFamilies(fams);
        setMetiers(mets);
        setSource('api');
      }
    } catch (err) {
      applyFallback();
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [applyFallback]);

  useEffect(() => {
    if (autoFetch) refresh();
  }, [autoFetch, refresh]);

  const getMetier = useCallback(
    (id) => {
      if (!id) return null;
      const key = normalizeCategorieId(id);
      return metiers.find((m) => normalizeCategorieId(m.id) === key) || null;
    },
    [metiers],
  );

  const getFamily = useCallback(
    (familyId) => families.find((f) => f.id === familyId) || null,
    [families],
  );

  const getMetiersByFamily = useCallback(
    (familyId) => metiers.filter((m) => (m.familyId || m.family) === familyId),
    [metiers],
  );

  const searchMetiers = useCallback(
    (query, familyId = null) => {
      const q = String(query || '').trim().toLowerCase();
      let list = familyId ? getMetiersByFamily(familyId) : metiers;
      if (!q) return list;
      return list.filter((m) => {
        const hay = [
          m.label,
          m.id,
          m.description,
          ...(Array.isArray(m.searchTerms) ? m.searchTerms : []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    },
    [metiers, getMetiersByFamily],
  );

  const afterMutation = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const createFamily = useCallback(
    async (body) => {
      const result = await apiService.createCommerceFamily(body);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const updateFamily = useCallback(
    async (id, body) => {
      const result = await apiService.updateCommerceFamily(id, body);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const deactivateFamily = useCallback(
    async (id) => {
      const result = await apiService.deactivateCommerceFamily(id);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const reactivateFamily = useCallback(
    async (id) => {
      const result = await apiService.reactivateCommerceFamily(id);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const archiveFamily = useCallback(
    async (id) => {
      const result = await apiService.archiveCommerceFamily(id);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const deleteFamily = useCallback(
    async (id) => {
      const result = await apiService.deleteCommerceFamily(id);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const createMetier = useCallback(
    async (body) => {
      const result = await apiService.createCommerceMetier(body);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const updateMetier = useCallback(
    async (id, body) => {
      const result = await apiService.updateCommerceMetier(id, body);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const deactivateMetier = useCallback(
    async (id) => {
      const result = await apiService.deactivateCommerceMetier(id);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const reactivateMetier = useCallback(
    async (id) => {
      const result = await apiService.reactivateCommerceMetier(id);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const archiveMetier = useCallback(
    async (id) => {
      const result = await apiService.archiveCommerceMetier(id);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const deprecateMetier = useCallback(
    async (id, body) => {
      const result = await apiService.deprecateCommerceMetier(id, body);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const deleteMetier = useCallback(
    async (id) => {
      const result = await apiService.deleteCommerceMetier(id);
      await afterMutation();
      return result;
    },
    [afterMutation],
  );

  const activeFamilies = useMemo(
    () => families.filter((f) => f.actif !== false && !f.archivedAt),
    [families],
  );

  const activeMetiers = useMemo(
    () => metiers.filter((m) => m.actif !== false && !m.archivedAt && !m.deprecated),
    [metiers],
  );

  return {
    families,
    metiers,
    activeFamilies,
    activeMetiers,
    loading,
    error,
    source,
    refresh,
    getMetier,
    getFamily,
    getMetiersByFamily,
    searchMetiers,
    createFamily,
    updateFamily,
    deactivateFamily,
    reactivateFamily,
    archiveFamily,
    deleteFamily,
    createMetier,
    updateMetier,
    deactivateMetier,
    reactivateMetier,
    archiveMetier,
    deprecateMetier,
    deleteMetier,
  };
}

export default useCommerceTaxonomy;
